import React, {
	createContext,
	type ComponentProps,
	type ReactNode,
	useContext,
	useEffect,
	useRef,
} from 'react';
import { useIntl } from 'react-intl';
import { getDocument } from '@atlaskit/browser-apis';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Image from '@atlaskit/image/image';
import { Box } from '@atlaskit/primitives/compiled';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useMessageInstances } from '@atlassian/post-office-headless-api/hooks/useMessageInstances';
import updateHelpCenterIllustration from './assets/update-help-center.svg';
import messages from './messages';
import { PostOfficeScope } from './post-office-scope';

export const MIGRATION_ONBOARDING_GATE = 'post-office_help-center-migration-onboarding';
export const MESSAGE_TEMPLATE_ID = 'jsm-help-center-management-update-help-center-onboarding';
/** The spotlight of the Post Office template anchored on the Update help center button. */
const MANAGEMENT_PAGE_FEATURE_ID = 'update-help-center';
/** The spotlight anchored on View all help centers in the avatar menu. */
const PROFILE_MENU_FEATURE_ID = 'update-help-center-profile-menu';

interface SpotlightMessage {
	context: { featureId?: string };
	messageTemplateId: typeof MESSAGE_TEMPLATE_ID;
}

// The card renders in place, so it would otherwise inherit its anchor's text styles: the page
// header actions' `white-space: nowrap` stops its copy wrapping, and the help center header's
// `line-height: 0` stacks the lines of its body on top of each other.
const styles = cssMap({
	card: {
		font: token(
			'font.body',
			'normal 400 0.875rem/1.25rem "Atlassian Sans", ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, "Helvetica Neue", sans-serif',
		),
		whiteSpace: 'normal',
	},
	highlightedItem: {
		backgroundColor: token('color.background.neutral.subtle.hovered', '#0515240F'),
	},
});

const useSpotlightMessage = (
	featureId: typeof MANAGEMENT_PAGE_FEATURE_ID | typeof PROFILE_MENU_FEATURE_ID,
) => {
	const { messageInstanceIds, PostOfficeMessageInstance, dismissMessage } =
		useMessageInstances<SpotlightMessage>(
			{ placementId: 'headless', messageTemplateId: MESSAGE_TEMPLATE_ID },
			{
				match: ({ data, matchBuilder }) =>
					matchBuilder.messageContext((context) => context.featureId === featureId).match(data),
			},
		);

	return { PostOfficeMessageInstance, dismissMessage, messageInstanceId: messageInstanceIds[0] };
};

type SpotlightMessageState = ReturnType<typeof useSpotlightMessage>;

interface UpdateHelpCenterSpotlightCardProps {
	children: ReactNode;
	message: SpotlightMessageState;
	placement: ComponentProps<typeof PopoverContent>['placement'];
	testId: string;
}

const UpdateHelpCenterSpotlightCard = ({
	children,
	message,
	placement,
	testId,
}: UpdateHelpCenterSpotlightCardProps) => {
	const { formatMessage } = useIntl();
	const { messageInstanceId, PostOfficeMessageInstance, dismissMessage } = message;

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			{messageInstanceId ? (
				<PostOfficeMessageInstance messageInstanceId={messageInstanceId}>
					{(_message, interactions) => {
						const handleGotIt = () => {
							interactions?.onAcknowledged({ interactionIdentifier: 'got-it' });
							dismissMessage(messageInstanceId);
						};
						const handleClose = (interactionIdentifier: string) => {
							interactions?.onClosed({ interactionIdentifier });
							dismissMessage(messageInstanceId);
						};

						return (
							<PopoverContent
								isVisible
								placement={placement}
								dismiss={() => handleClose('popover-dismiss')}
							>
								<Box xcss={styles.card}>
									<SpotlightCard testId={testId}>
										<SpotlightHeader>
											<SpotlightHeadline>{formatMessage(messages.headline)}</SpotlightHeadline>
											<SpotlightControls>
												{/*
												 * The spotlight opens on page load, so the default `autoFocus` would pull
												 * focus onto the cross and let a stray Enter end a once-only message.
												 */}
												<Tooltip content={formatMessage(messages.dismiss)}>
													<SpotlightDismissControl
														autoFocus={false}
														onClick={() => handleClose('dismiss')}
														testId={`${testId}-dismiss`}
													/>
												</Tooltip>
											</SpotlightControls>
										</SpotlightHeader>
										<SpotlightMedia>
											{/* Decorative: the body text says everything the illustration shows. */}
											<Image
												src={updateHelpCenterIllustration}
												alt=""
												testId={`${testId}-illustration`}
											/>
										</SpotlightMedia>
										<SpotlightBody>{formatMessage(messages.body)}</SpotlightBody>
										<SpotlightFooter>
											<SpotlightActions>
												<SpotlightPrimaryAction onClick={handleGotIt} testId={`${testId}-got-it`}>
													{formatMessage(messages.gotIt)}
												</SpotlightPrimaryAction>
											</SpotlightActions>
										</SpotlightFooter>
									</SpotlightCard>
								</Box>
							</PopoverContent>
						);
					}}
				</PostOfficeMessageInstance>
			) : null}
		</PopoverProvider>
	);
};

/**
 * Points existing admins at the Update help center button. Renders only `children` while the gate
 * is off or during server rendering, so no Post Office request is made in either case.
 */
export const UpdateHelpCenterSpotlight = ({ children }: { children: ReactNode }) => {
	if (!getDocument() || !FeatureGates.checkGate(MIGRATION_ONBOARDING_GATE)) {
		return <>{children}</>;
	}

	return (
		<PostOfficeScope>
			<ManagementPageSpotlight>{children}</ManagementPageSpotlight>
		</PostOfficeScope>
	);
};

const ManagementPageSpotlight = ({ children }: { children: ReactNode }) => {
	const message = useSpotlightMessage(MANAGEMENT_PAGE_FEATURE_ID);

	return (
		<UpdateHelpCenterSpotlightCard
			message={message}
			placement="bottom-start"
			testId="update-help-center-spotlight"
		>
			{children}
		</UpdateHelpCenterSpotlightCard>
	);
};

const ProfileMenuSpotlightContext = createContext<SpotlightMessageState | null>(null);

interface ProfileMenuUpdateSpotlightProviderProps {
	children: ReactNode;
	/** Whether View all help centers, the spotlight's anchor, is shown in the menu. */
	isEnabled: boolean;
	isMenuOpen: boolean;
	onOpenMenu: () => void;
}

/**
 * Fetches the avatar menu spotlight on page load and opens the menu when it's eligible. It sits
 * above the menu so the message, and its dismissal, outlive the menu closing.
 */
export const ProfileMenuUpdateSpotlightProvider = ({
	children,
	isEnabled,
	...props
}: ProfileMenuUpdateSpotlightProviderProps) => {
	if (!isEnabled || !getDocument() || !FeatureGates.checkGate(MIGRATION_ONBOARDING_GATE)) {
		return <>{children}</>;
	}

	return (
		<PostOfficeScope>
			<ProfileMenuSpotlightMessage {...props}>{children}</ProfileMenuSpotlightMessage>
		</PostOfficeScope>
	);
};

const ProfileMenuSpotlightMessage = ({
	children,
	isMenuOpen,
	onOpenMenu,
}: Omit<ProfileMenuUpdateSpotlightProviderProps, 'isEnabled'>) => {
	const message = useSpotlightMessage(PROFILE_MENU_FEATURE_ID);
	const { messageInstanceId, dismissMessage } = message;
	const hasMenuOpened = useRef(false);

	useEffect(() => {
		if (messageInstanceId) {
			onOpenMenu();
		}
	}, [messageInstanceId, onOpenMenu]);

	// Closing the menu any other way, e.g. from the avatar or Escape, ends the spotlight too.
	useEffect(() => {
		if (!messageInstanceId) {
			return;
		}
		if (isMenuOpen) {
			hasMenuOpened.current = true;
		} else if (hasMenuOpened.current) {
			dismissMessage(messageInstanceId);
		}
	}, [dismissMessage, isMenuOpen, messageInstanceId]);

	return (
		<ProfileMenuSpotlightContext.Provider value={message}>
			{children}
		</ProfileMenuSpotlightContext.Provider>
	);
};

/** Anchors the avatar menu spotlight on View all help centers. */
export const ProfileMenuUpdateSpotlight = ({ children }: { children: ReactNode }) => {
	const message = useContext(ProfileMenuSpotlightContext);

	if (!message) {
		return <>{children}</>;
	}

	return (
		<UpdateHelpCenterSpotlightCard
			message={message}
			placement="left-end"
			testId="avatar-menu-update-help-center-spotlight"
		>
			{/* Shows the item as hovered while the spotlight points at it. */}
			<Box
				xcss={message.messageInstanceId ? styles.highlightedItem : undefined}
				testId="avatar-menu-update-help-center-spotlight-target"
			>
				{children}
			</Box>
		</UpdateHelpCenterSpotlightCard>
	);
};
